import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Save, X, Sparkles, Play, Pause, Volume2, PawPrint, User, Skull, Cpu, SlidersHorizontal } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { API, BACKEND_URL } from "../lib/constants";
import VoiceBlendControls from "../components/VoiceBlendControls";

const CHARACTER_TYPES = [
  { value: "person", label: "Person", icon: User, desc: "Human character with full appearance builder" },
  { value: "animal", label: "Animal", icon: PawPrint, desc: "Animal character — description drives the image" },
  { value: "beast", label: "Beast", icon: Skull, desc: "Mythical creature — description drives the image" },
  { value: "other", label: "Other", icon: Cpu, desc: "Robot, alien, object — description drives the image" },
];

const GENDER_OPTIONS = ["Female", "Male", "Non-binary"];
const AGE_OPTIONS = ["Child", "Teen", "Young Adult", "Adult", "Middle-aged", "Senior"];
const SKIN_TONE_OPTIONS = ["Fair", "Light", "Medium", "Olive", "Caramel", "Tan", "Brown", "Dark Brown", "Deep", "Ebony"];
const HAIR_TYPE_OPTIONS = ["Straight - Fine", "Straight - Thick", "Wavy", "Curly", "Coily", "Kinky", "Afro", "Locs", "Braids", "Bald"];
const HAIR_COLOR_OPTIONS = ["Black", "Dark Brown", "Brown", "Auburn", "Red", "Strawberry Blonde", "Blonde", "Platinum", "Gray", "White", "Silver"];
const HAIR_LENGTH_OPTIONS = ["Bald", "Buzz", "Short", "Medium", "Long", "Very Long"];
const FACE_SHAPE_OPTIONS = ["Oval", "Round", "Square", "Heart", "Diamond", "Oblong", "Triangle"];
const BODY_TYPE_OPTIONS = ["Petite (small frame, short)", "Slim", "Average", "Athletic", "Muscular", "Curvy", "Plus-size", "Heavyset"];
const EYE_COLOR_OPTIONS = ["Brown", "Dark Brown", "Hazel", "Amber", "Green", "Blue", "Gray", "Black"];
const FACIAL_HAIR_OPTIONS = ["None", "Stubble", "Mustache", "Goatee", "Full Beard", "Short Beard", "Long Beard"];
const CLOTHING_OPTIONS = ["Casual", "Formal", "Business", "Streetwear", "Bohemian", "Sporty", "Gothic", "Vintage", "Elegant"];
const ACCESSORY_OPTIONS = ["glasses", "sunglasses", "gold jewelry", "silver jewelry", "headwrap", "hat", "crown", "earrings", "nose ring", "watch", "scarf", "bandana"];

const VOICE_OPTIONS = [
  { id: "none", name: "No Voice", desc: "Text only" },
  { id: "alloy", name: "Alloy", desc: "Warm, friendly" },
  { id: "ash", name: "Ash", desc: "Calm, assertive" },
  { id: "coral", name: "Coral", desc: "Energetic, playful" },
  { id: "echo", name: "Echo", desc: "Mysterious, subtle" },
  { id: "fable", name: "Fable", desc: "Expressive, storytelling" },
  { id: "nova", name: "Nova", desc: "Ambitious, driven" },
  { id: "onyx", name: "Onyx", desc: "Wise, reflective" },
  { id: "sage", name: "Sage", desc: "Calm, wise" },
  { id: "shimmer", name: "Shimmer", desc: "Gentle, soothing" },
];

const DEFAULT_FORM = {
  name: "", description: "", nickname: "", series: "the-red-ink-chronicles",
  character_type: "person", custom_image_prompt: "",
  gender: "", age_range: "", skin_tone: "", hair_type: "", hair_color: "", hair_length: "",
  face_shape: "", body_type: "", eye_color: "", facial_hair: "", clothing_style: "",
  height: "", weight: "", accessories: [], distinguishing_features: "",
  ai_voice: "none", ai_speed: 1.0, pitch_shift: 0, traits: [], resonance: "", pronunciation: "",
};

function SelectField({ label, value, onChange, options, testId }) {
  const { isDark } = useTheme();
  return (
    <div>
      <label className="block text-[10px] font-mono tracking-widest text-gray-500 mb-1.5">{label}</label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2.5 rounded border text-sm ${isDark ? 'bg-[#111] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:border-amber-500 focus:outline-none transition-colors`}
        data-testid={testId}
      >
        <option value="">Select {label.toLowerCase()}</option>
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    </div>
  );
}

export default function CharacterEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const isEditing = !!id;

  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewingVoice, setPreviewingVoice] = useState(false);
  const [blendAudioUrl, setBlendAudioUrl] = useState(null);
  const [showBlend, setShowBlend] = useState(false);
  const voiceAudioRef = useRef(null);

  const isPerson = form.character_type === "person";

  useEffect(() => {
    if (id) {
      axios.get(`${API}/characters/${id}`).then(res => {
        const c = res.data;
        setForm({
          name: c.name || "", description: c.description || "", nickname: c.nickname || "",
          series: c.series || "the-red-ink-chronicles",
          character_type: c.character_type || "person", custom_image_prompt: c.custom_image_prompt || "",
          gender: c.gender || "", age_range: c.age_range || "", skin_tone: c.skin_tone || "",
          hair_type: c.hair_type || "", hair_color: c.hair_color || "", hair_length: c.hair_length || "",
          face_shape: c.face_shape || "", body_type: c.body_type || "", eye_color: c.eye_color || "",
          facial_hair: c.facial_hair || "", clothing_style: c.clothing_style || "",
          height: c.height || "", weight: c.weight || "",
          accessories: c.accessories || [], distinguishing_features: c.distinguishing_features || "",
          ai_voice: c.ai_voice || "none", ai_speed: c.ai_speed || 1.0, pitch_shift: c.pitch_shift || 0,
          traits: c.traits || [], resonance: c.resonance || "", pronunciation: c.pronunciation || "",
        });
        const imgSrc = c.preview_image_url || c.preview_image;
        if (imgSrc) setPreviewUrl(imgSrc.startsWith("http") ? imgSrc : `${BACKEND_URL}${imgSrc}`);
        else if (c.image_url) setPreviewUrl(c.image_url.startsWith('http') ? c.image_url : `${BACKEND_URL}${c.image_url}`);
      }).catch(console.error).finally(() => setLoading(false));
    }
  }, [id]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const toggleAccessory = (acc) => {
    setForm(prev => ({
      ...prev,
      accessories: prev.accessories.includes(acc)
        ? prev.accessories.filter(a => a !== acc)
        : [...prev.accessories, acc]
    }));
  };

  const buildAppearanceDesc = () => {
    if (!isPerson) return form.description || "Describe this character above";
    const parts = [];
    if (form.age_range) parts.push(form.age_range.toLowerCase());
    if (form.gender) parts.push(form.gender.toLowerCase());
    if (form.skin_tone) parts.push(`${form.skin_tone.toLowerCase()} skin`);
    if (form.hair_color && form.hair_type) parts.push(`${form.hair_color.toLowerCase()} ${form.hair_type.toLowerCase()}`);
    if (form.hair_length) parts.push(`${form.hair_length.toLowerCase()} hair`);
    if (form.body_type) parts.push(`${form.body_type.toLowerCase()} build`);
    if (form.clothing_style) parts.push(form.clothing_style.toLowerCase());
    return parts.join(" - ") || "No appearance details set";
  };

  const handleSave = async () => {
    if (!form.name.trim()) return alert("Name is required");
    setSaving(true);
    try {
      const payload = { ...form, ai_voice: form.ai_voice === "none" ? null : form.ai_voice };
      if (isEditing) {
        await axios.put(`${API}/characters/${id}`, payload);
      } else {
        await axios.post(`${API}/characters`, payload);
      }
      navigate("/bible");
    } catch (e) {
      alert("Failed to save: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const handleGeneratePreview = async () => {
    if (!form.name.trim()) return alert("Add a name first");
    if (!isPerson && !form.description.trim() && !form.custom_image_prompt.trim()) {
      return alert("Add a description or custom image prompt first");
    }
    const charId = id;
    if (!charId) {
      setSaving(true);
      try {
        const payload = { ...form, ai_voice: form.ai_voice === "none" ? null : form.ai_voice };
        const res = await axios.post(`${API}/characters`, payload);
        const newId = res.data.id;
        setSaving(false);
        setGenerating(true);
        const imgRes = await axios.post(`${API}/characters/${newId}/generate-preview`);
        const imgUrl = imgRes.data.preview_image_url;
        setPreviewUrl(imgUrl?.startsWith("http") ? imgUrl : `${BACKEND_URL}${imgUrl}`);
        navigate(`/characters/${newId}/edit`, { replace: true });
      } catch (e) {
        alert("Failed: " + (e.response?.data?.detail || e.message));
      } finally { setSaving(false); setGenerating(false); }
      return;
    }
    setGenerating(true);
    try {
      const payload = { ...form, ai_voice: form.ai_voice === "none" ? null : form.ai_voice };
      await axios.put(`${API}/characters/${charId}`, payload);
      const res = await axios.post(`${API}/characters/${charId}/generate-preview`);
      const imgUrl = res.data.preview_image_url;
      setPreviewUrl(imgUrl?.startsWith("http") ? imgUrl : `${BACKEND_URL}${imgUrl}`);
    } catch (e) {
      alert("Image generation failed: " + (e.response?.data?.detail || e.message));
    } finally { setGenerating(false); }
  };

  const handlePreviewVoice = async () => {
    if (!form.ai_voice || form.ai_voice === "none") return;
    if (previewingVoice && voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current = null;
      setPreviewingVoice(false);
      return;
    }
    setPreviewingVoice(true);
    try {
      const sampleText = form.description
        ? `${form.name || "I am a character"}. ${form.description.substring(0, 200)}`
        : `Hello, my name is ${form.name || "this character"}. This is how I sound when reading a line from the script.`;
      const res = await fetch(`${API}/voice/quick-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ voice: form.ai_voice, text: sampleText, speed: form.ai_speed }),
      });
      if (!res.ok) throw new Error("Voice preview failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setBlendAudioUrl(url);
      const audio = new Audio(url);
      voiceAudioRef.current = audio;
      audio.onended = () => { setPreviewingVoice(false); };
      audio.onerror = () => { setPreviewingVoice(false); };
      audio.play();
    } catch (e) {
      console.error("Voice preview error:", e);
      alert("Voice preview failed. Check that the AI service is configured.");
      setPreviewingVoice(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen pt-24 pb-32 px-6 ${isDark ? '' : 'text-gray-900'}`} data-testid="character-editor-page">
      <div className="max-w-6xl mx-auto">
        <header className="mb-8">
          <h1 className="font-cinzel text-3xl md:text-4xl font-bold">
            {isEditing ? "Edit" : "Create"} <span className="text-amber-500">Character</span>
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
            Powered by <span className="text-amber-500 font-semibold">ToL</span> — create any character your mind imagines.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2 space-y-8">

            {/* Character Type — Powered by ToL */}
            <section>
              <h2 className="font-cinzel text-lg font-bold mb-4">Character Type</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="char-type-picker">
                {CHARACTER_TYPES.map(t => {
                  const Icon = t.icon;
                  return (
                    <button key={t.value} onClick={() => update("character_type", t.value)}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        form.character_type === t.value
                          ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                          : isDark ? 'border-white/10 hover:border-amber-500/30 text-gray-300' : 'border-gray-200 hover:border-amber-400 text-gray-700'
                      }`}
                      data-testid={`type-${t.value}`}
                    >
                      <Icon size={20} className={form.character_type === t.value ? 'text-amber-400' : 'text-gray-500'} />
                      <p className="font-bold text-sm mt-2">{t.label}</p>
                      <p className={`text-[10px] mt-0.5 ${form.character_type === t.value ? 'text-amber-500/70' : 'text-gray-500'}`}>{t.desc}</p>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Basic Info */}
            <section>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-mono tracking-widest text-gray-500 mb-1.5">NAME</label>
                  <input type="text" value={form.name} onChange={e => update("name", e.target.value)}
                    className={`w-full px-3 py-2.5 rounded border text-sm ${isDark ? 'bg-[#111] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:border-amber-500 focus:outline-none`}
                    placeholder="Character name" data-testid="char-name" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono tracking-widest text-gray-500 mb-1.5">DESCRIPTION</label>
                  <input type="text" value={form.description} onChange={e => update("description", e.target.value)}
                    className={`w-full px-3 py-2.5 rounded border text-sm ${isDark ? 'bg-[#111] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:border-amber-500 focus:outline-none`}
                    placeholder={isPerson ? "Short description..." : "e.g. 5 lb Yorkie with big brown eyes, tiny red bandana"} data-testid="char-description" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-[10px] font-mono tracking-widest text-gray-500 mb-1.5">NICKNAME</label>
                  <input type="text" value={form.nickname} onChange={e => update("nickname", e.target.value)}
                    className={`w-full px-3 py-2.5 rounded border text-sm ${isDark ? 'bg-[#111] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:border-amber-500 focus:outline-none`}
                    placeholder="Alias / nickname" data-testid="char-nickname" />
                </div>
                <div>
                  <label className="block text-[10px] font-mono tracking-widest text-gray-500 mb-1.5">NAME PRONUNCIATION</label>
                  <input type="text" value={form.pronunciation} onChange={e => update("pronunciation", e.target.value)}
                    className={`w-full px-3 py-2.5 rounded border text-sm ${isDark ? 'bg-[#111] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:border-amber-500 focus:outline-none`}
                    placeholder='e.g. "LAY-lan" or "seh-LEE-nah"' data-testid="char-pronunciation" />
                </div>
              </div>
            </section>

            {/* Custom Image Prompt — always available */}
            <section>
              <label className="block text-[10px] font-mono tracking-widest text-gray-500 mb-1.5">
                CUSTOM IMAGE PROMPT <span className="text-amber-500">(overrides appearance builder)</span>
              </label>
              <textarea
                value={form.custom_image_prompt}
                onChange={e => update("custom_image_prompt", e.target.value)}
                rows={3}
                className={`w-full px-3 py-2.5 rounded border text-sm resize-y ${isDark ? 'bg-[#111] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:border-amber-500 focus:outline-none`}
                placeholder={isPerson
                  ? "Leave blank to use appearance fields, or type exactly what you want the AI to generate..."
                  : "Describe exactly what you want: e.g. 'Tiny 5 lb Yorkie with huge expressive brown eyes, wearing a red bandana, sitting alert, cinematic noir lighting'"
                }
                data-testid="char-custom-prompt"
              />
            </section>

            {/* Appearance — only for Person type */}
            {isPerson && (
              <>
                <section>
                  <h2 className="font-cinzel text-lg font-bold mb-4">Appearance</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <SelectField label="GENDER" value={form.gender} onChange={v => update("gender", v)} options={GENDER_OPTIONS} testId="char-gender" />
                    <SelectField label="AGE RANGE" value={form.age_range} onChange={v => update("age_range", v)} options={AGE_OPTIONS} testId="char-age-range" />
                    <SelectField label="SKIN TONE" value={form.skin_tone} onChange={v => update("skin_tone", v)} options={SKIN_TONE_OPTIONS} testId="char-skin-tone" />
                    <SelectField label="HAIR TYPE" value={form.hair_type} onChange={v => update("hair_type", v)} options={HAIR_TYPE_OPTIONS} testId="char-hair-type" />
                    <SelectField label="HAIR COLOR" value={form.hair_color} onChange={v => update("hair_color", v)} options={HAIR_COLOR_OPTIONS} testId="char-hair-color" />
                    <SelectField label="HAIR LENGTH" value={form.hair_length} onChange={v => update("hair_length", v)} options={HAIR_LENGTH_OPTIONS} testId="char-hair-length" />
                    <SelectField label="FACE SHAPE" value={form.face_shape} onChange={v => update("face_shape", v)} options={FACE_SHAPE_OPTIONS} testId="char-face-shape" />
                    <SelectField label="BODY TYPE" value={form.body_type} onChange={v => update("body_type", v)} options={BODY_TYPE_OPTIONS} testId="char-body-type" />
                    <SelectField label="EYE COLOR" value={form.eye_color} onChange={v => update("eye_color", v)} options={EYE_COLOR_OPTIONS} testId="char-eye-color" />
                    <SelectField label="FACIAL HAIR" value={form.facial_hair} onChange={v => update("facial_hair", v)} options={FACIAL_HAIR_OPTIONS} testId="char-facial-hair" />
                    <SelectField label="CLOTHING STYLE" value={form.clothing_style} onChange={v => update("clothing_style", v)} options={CLOTHING_OPTIONS} testId="char-clothing" />
                  </div>
                </section>

                <section>
                  <h2 className="font-cinzel text-lg font-bold mb-4">Build Details</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-mono tracking-widest text-gray-500 mb-1.5">HEIGHT</label>
                      <input type="text" value={form.height} onChange={e => update("height", e.target.value)}
                        className={`w-full px-3 py-2.5 rounded border text-sm ${isDark ? 'bg-[#111] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:border-amber-500 focus:outline-none`}
                        placeholder="5'1&quot;" data-testid="char-height" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-mono tracking-widest text-gray-500 mb-1.5">WEIGHT</label>
                      <input type="text" value={form.weight} onChange={e => update("weight", e.target.value)}
                        className={`w-full px-3 py-2.5 rounded border text-sm ${isDark ? 'bg-[#111] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:border-amber-500 focus:outline-none`}
                        placeholder="120" data-testid="char-weight" />
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="font-cinzel text-lg font-bold mb-4">Accessories</h2>
                  <div className="flex flex-wrap gap-2" data-testid="char-accessories">
                    {ACCESSORY_OPTIONS.map(acc => (
                      <button key={acc} onClick={() => toggleAccessory(acc)}
                        className={`px-4 py-2 text-sm rounded-full border transition-all ${
                          form.accessories.includes(acc)
                            ? 'bg-amber-500 border-amber-400 text-black font-semibold'
                            : isDark ? 'border-white/15 text-gray-400 hover:border-amber-500/40' : 'border-gray-300 text-gray-600 hover:border-amber-400'
                        }`}
                        data-testid={`acc-${acc.replace(/\s+/g, '-')}`}
                      >
                        {acc}
                      </button>
                    ))}
                  </div>
                </section>
              </>
            )}

            {/* Distinguishing Features — always shown */}
            <section>
              <label className="block text-[10px] font-mono tracking-widest text-gray-500 mb-1.5">DISTINGUISHING FEATURES</label>
              <input type="text" value={form.distinguishing_features} onChange={e => update("distinguishing_features", e.target.value)}
                className={`w-full px-3 py-2.5 rounded border text-sm ${isDark ? 'bg-[#111] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:border-amber-500 focus:outline-none`}
                placeholder={isPerson ? "Tattoos, scars, dimples, freckles..." : "Distinctive markings, unique features, colors..."}
                data-testid="char-features" />
            </section>

            {/* Character Voice — READ ONLY (edit in Voice Lab) */}
            <section>
              <h2 className="font-cinzel text-lg font-bold mb-2">Character Voice</h2>
              <div className={`p-4 rounded-lg border ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-gray-50'}`}>
                {form.ai_voice && form.ai_voice !== "none" ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-gray-500">Voice:</span>
                      <span className="text-white font-bold">{VOICE_OPTIONS.find(v => v.id === form.ai_voice)?.name || form.ai_voice}</span>
                      <span className="text-gray-500">Speed:</span>
                      <span className="text-white font-bold">{form.ai_speed?.toFixed(1) || "1.0"}x</span>
                      <span className="text-gray-500">Pitch:</span>
                      <span className="text-white font-bold">{form.pitch_shift || 0}st</span>
                    </div>
                    <p className="text-xs text-amber-400/70">
                      Voice settings are managed in <strong>Production → Assets → Voice Lab</strong>. Create or edit a profile with this character's exact name to auto-assign.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">
                    No voice assigned. Go to <strong>Production → Assets → Voice Lab</strong> and create a profile named exactly "<strong>{form.name || "this character"}</strong>" to auto-assign.
                  </p>
                )}
              </div>
            </section>

            {/* Series Assignment */}
            <section>
              <h2 className="font-cinzel text-lg font-bold mb-4">Series</h2>
              <div className="flex gap-3">
                {[
                  { slug: "the-red-ink-chronicles", label: "The Red Ink Chronicles" },
                  { slug: "the-notarys-shadow", label: "The Notary's Shadow" },
                  { slug: "signed-sealed-betrayed", label: "Signed, Sealed, Betrayed" }
                ].map(s => (
                  <button key={s.slug} onClick={() => update("series", s.slug)}
                    className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                      form.series === s.slug ? 'bg-red-600 text-white' : isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`series-${s.slug}-btn`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </section>

            {/* Action Buttons */}
            <div className="flex items-center gap-4 pt-4 border-t border-white/10">
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded transition-colors disabled:opacity-50"
                data-testid="save-character-btn">
                {saving ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <Save size={18} />}
                {isEditing ? "Update Character" : "Create Character"}
              </button>
              <button onClick={() => navigate("/bible")}
                className={`flex items-center gap-2 px-6 py-3 rounded font-semibold transition-colors ${isDark ? 'bg-white/10 hover:bg-white/15 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                data-testid="cancel-btn">
                <X size={18} /> Cancel
              </button>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="lg:col-span-1">
            <div className={`sticky top-28 rounded-lg border p-6 ${isDark ? 'border-white/10 bg-[#0a0a0a]' : 'border-gray-200 bg-white'}`} data-testid="character-preview-panel">
              <h2 className="font-cinzel text-lg font-bold mb-4">Preview</h2>

              <div className={`aspect-[3/4] rounded-lg overflow-hidden mb-4 flex items-center justify-center ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                {generating ? (
                  <div className="text-center">
                    <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-xs text-gray-500">Generating preview...</p>
                  </div>
                ) : previewUrl ? (
                  <img src={previewUrl} alt={form.name || "Character"} className="w-full h-full object-cover" data-testid="preview-image" />
                ) : (
                  <div className="text-center px-4">
                    <div className={`w-24 h-24 rounded-full mx-auto mb-3 flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                      {isPerson
                        ? <span className="text-3xl font-cinzel font-bold text-gray-500">{form.name ? form.name[0] : "?"}</span>
                        : React.createElement(CHARACTER_TYPES.find(t => t.value === form.character_type)?.icon || User, { size: 32, className: "text-gray-500" })
                      }
                    </div>
                    <p className="text-xs text-gray-500">
                      {isPerson ? "Fill in appearance details and generate a preview" : "Add a description and generate a preview"}
                    </p>
                  </div>
                )}
              </div>

              <button onClick={handleGeneratePreview} disabled={generating || !form.name.trim()}
                className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded transition-colors flex items-center justify-center gap-2 disabled:opacity-50 mb-4"
                data-testid="generate-preview-btn">
                {generating ? (
                  <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles size={16} /> Generate Preview</>
                )}
              </button>

              <div className={`p-3 rounded text-xs ${isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-600'}`} data-testid="appearance-desc">
                <p className="font-mono text-[10px] text-gray-500 mb-1">
                  {form.custom_image_prompt ? "Custom Prompt:" : isPerson ? "Appearance:" : "Description:"}
                </p>
                <p>{form.custom_image_prompt || buildAppearanceDesc()}</p>
              </div>

              <p className={`text-[10px] text-center mt-3 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                Powered by <span className="text-amber-500 font-semibold">ToL</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
